import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { cn } from "../../lib/utils";
const AccordionCtx = React.createContext(null);
/** Matches the prototype's `--expand-enter-duration` / `--expand-exit-duration`. */
const ENTER_MS = 480;
const EXIT_MS = 360;
const Accordion = React.forwardRef(({ className, type = "single", defaultOpen, open, onOpenChange, children, ...props }, ref) => {
    const [internal, setInternal] = React.useState(() => new Set(defaultOpen ?? []));
    const openItems = open ? new Set(open) : internal;
    const toggle = React.useCallback((id) => {
        const next = new Set(openItems);
        if (next.has(id))
            next.delete(id);
        else {
            if (type === "single")
                next.clear();
            next.add(id);
        }
        if (open === undefined)
            setInternal(next);
        onOpenChange?.([...next]);
    }, [openItems, type, open, onOpenChange]);
    return (_jsx(AccordionCtx.Provider, { value: { openItems, toggle }, children: _jsx("div", { ref: ref, className: cn("w-full", className), ...props, children: children }) }));
});
Accordion.displayName = "Accordion";
/**
 * Drives the reveal in two steps so a body can animate both ways without ever
 * existing before a visitor asks for it.
 *
 * `mounted` latches on the first open and never releases — from then on the
 * body stays in the DOM and collapses under CSS, which is what buys the exit
 * animation. `expanded` lags `mounted` by a frame so the first open starts from
 * `0fr` and actually transitions; mounting straight into the open state would
 * simply appear.
 *
 * The latch is the load-bearing part: until a visitor opens something there is
 * no panel at all, which is the behaviour a closed accordion is read by.
 */
function useReveal(isOpen) {
    const [mounted, setMounted] = React.useState(isOpen);
    const [expanded, setExpanded] = React.useState(isOpen);
    React.useEffect(() => {
        if (!isOpen) {
            setExpanded(false);
            return;
        }
        if (!mounted) {
            setMounted(true);
            return;
        }
        const frame = requestAnimationFrame(() => setExpanded(true));
        return () => cancelAnimationFrame(frame);
    }, [isOpen, mounted]);
    return { mounted, expanded };
}
function AccordionItem({ id, title, children, className }) {
    const ctx = React.useContext(AccordionCtx);
    if (!ctx)
        throw new Error("AccordionItem must be used inside <Accordion>");
    const isOpen = ctx.openItems.has(id);
    const panelId = `accordion-panel-${id}`;
    const buttonId = `accordion-trigger-${id}`;
    const { mounted, expanded } = useReveal(isOpen);
    return (_jsxs("div", { className: cn("border-b border-primary/25 last:border-b-0", className), children: [_jsx("h3", { className: "m-0", children: _jsxs("button", { type: "button", id: buttonId, "aria-expanded": isOpen, "aria-controls": panelId, onClick: () => ctx.toggle(id), className: cn("flex w-full min-h-[76px] items-center justify-between gap-6 py-[22px] text-left", "@min-[901px]:min-h-[81px] @min-[901px]:gap-8 @min-[901px]:pb-[25px] @min-[901px]:pt-6", "text-foreground transition-colors hover:text-brand-blue-dark", "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"), children: [_jsx("span", { className: "font-sans text-[clamp(23px,6.7cqw,30px)] font-light leading-[1.12] tracking-[-0.02em] text-pretty @min-[901px]:text-[31px]", children: title }), _jsxs("span", { "aria-hidden": "true", className: "relative mr-[3px] block size-[15px] shrink-0 text-primary", children: [_jsx("span", { className: "absolute left-px top-[7px] h-px w-[13px] bg-current" }), _jsx("span", { className: cn("absolute left-px top-[7px] h-px w-[13px] bg-current", "transition-transform duration-[220ms] ease-out motion-reduce:transition-none", isOpen ? "rotate-0" : "rotate-90") })] })] }) }), mounted && (_jsx("div", { id: panelId, role: "region", "aria-labelledby": buttonId, "aria-hidden": !expanded, inert: !expanded || undefined, className: cn("grid motion-reduce:transition-none", expanded ? "visible grid-rows-[1fr]" : "invisible grid-rows-[0fr]"), style: {
                    // Visibility flips in at once but only out after the collapse has
                    // finished, so a closing body stays on screen while it shrinks.
                    transition: expanded
                        ? `grid-template-rows ${ENTER_MS}ms var(--ease-out), visibility 0s linear 0s`
                        : `grid-template-rows ${EXIT_MS}ms var(--ease-out), visibility 0s linear ${EXIT_MS}ms`,
                }, children: _jsx("div", { className: cn("min-h-0 w-full overflow-hidden transition-[opacity,transform] motion-reduce:transition-none", expanded
                        ? "translate-y-0 opacity-100 duration-[480ms] ease-out delay-[70ms]"
                        : "-translate-y-3 opacity-0 duration-[220ms] ease-in"), children: children }) }))] }));
}
export { Accordion, AccordionItem };
//# sourceMappingURL=accordion.js.map